import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const BRANDS_PATH = 'afosto.tenant.odr.orders.filters';

const OrderFilterSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsloading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    brands: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.filtersField.label),
        suggestionLabelKey: 'name',
        suggestionValueKey: 'id',
        url: '/odr/filters/keys',
        autocomplete: 'off',
        multiple: true,
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const { data: profileData = {} } = await profileApi.updateProfile(
        BRANDS_PATH.replace('tenant', pathID),
        fields,
      );
      setBrands(profileData?.brands || []);
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderFiltersSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderFiltersSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = {} } = await profileApi.getProfile(
          BRANDS_PATH.replace('tenant', pathID),
        );

        setBrands(profileData?.brands || []);
      } catch (error) {
        if (isMounted) {
          setBrands([]);
        }
      } finally {
        if (isMounted) {
          setIsloading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data } = await profileApi.getProfileDefinitions(
          BRANDS_PATH.replace('tenant', pathID),
        );
        if (isMounted) {
          setFormSchema(data);
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.display.title)}
              secondary={brands?.join(', ')}
            />
            {hasPermissions(['odr:orders:write']) && (
              <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            )}
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={{ brands }}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

OrderFilterSettings.propTypes = {
  pathID: PropTypes.string,
};

OrderFilterSettings.defaultProps = {
  pathID: 'tenant',
};

export default OrderFilterSettings;
